import * as React from 'react';
import { useTenant } from '@/hooks/use-tenant';
import {
    useGetProjectDocsQuery,
    useCreateProjectDocMutation,
    useUpdateProjectDocMutation,
    useDeleteProjectDocMutation,
    useGetExternalProjectDocsQuery,
    useCreateExternalProjectDocMutation,
    useUpdateExternalProjectDocMutation,
    useDeleteExternalProjectDocMutation,
} from '@/state/project/project-api';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
    Plus,
    FileText,
    Search,
    Maximize2,
    Calendar,
    User,
    Pencil,
    Trash2,
    FileUp,
} from 'lucide-react';
import type { ProjectDoc } from '@/types';
import { formatDate, isValid } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DocEditorModal } from './doc-editor-modal';
import { DocViewModal } from './doc-view-modal';

interface ProjectDocsProps {
    projectId: number;
    initialDocs?: ProjectDoc[];
    isExternal?: boolean;
    resourceId?: number;
    isReadOnly?: boolean;
}

const safeFormatDate = (dateStr: string | undefined | null, formatStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    if (!isValid(date)) return 'N/A';
    return formatDate(date, formatStr);
};

export const ProjectDocs = ({
    projectId,
    initialDocs,
    isExternal = false,
    resourceId,
    isReadOnly = false,
}: ProjectDocsProps) => {
    const { tenant } = useTenant();
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isEditorOpen, setIsEditorOpen] = React.useState(false);
    const [editingDoc, setEditingDoc] = React.useState<ProjectDoc | undefined>();
    const [viewingDoc, setViewingDoc] = React.useState<ProjectDoc | undefined>();

    // Internal Project Hooks
    const {
        data: fetchedInternalDocs,
        isLoading: internalLoading,
        error: internalError,
        refetch: refetchInternal
    } = useGetProjectDocsQuery({ tenant: tenant!, projectId }, {
        skip: !!initialDocs || isExternal
    });
    const [createInternalDoc, { isLoading: isCreatingInternal }] = useCreateProjectDocMutation();
    const [updateInternalDoc, { isLoading: isUpdatingInternal }] = useUpdateProjectDocMutation();
    const [deleteInternalDoc] = useDeleteProjectDocMutation();

    // External Project Hooks
    const {
        data: fetchedExternalDocs,
        isLoading: externalLoading,
        error: externalError,
        refetch: refetchExternal
    } = useGetExternalProjectDocsQuery({
        projectId: resourceId || projectId
    }, {
        skip: !!initialDocs || !isExternal
    });
    const [createExternalDoc, { isLoading: isCreatingExternal }] = useCreateExternalProjectDocMutation();
    const [updateExternalDoc, { isLoading: isUpdatingExternal }] = useUpdateExternalProjectDocMutation();
    const [deleteExternalDoc] = useDeleteExternalProjectDocMutation();

    const docs = initialDocs || (isExternal ? fetchedExternalDocs : fetchedInternalDocs);
    const docsLoading = isExternal ? externalLoading : internalLoading;
    const docsError = isExternal ? externalError : internalError;
    const refetch = isExternal ? refetchExternal : refetchInternal;

    const isCreating = isExternal ? isCreatingExternal : isCreatingInternal;
    const isUpdating = isExternal ? isUpdatingExternal : isUpdatingInternal;

    const filteredDocs = React.useMemo(() => {
        if (!docs) return [];
        return docs.filter(doc =>
            doc.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [docs, searchTerm]);

    const handleSaveDoc = async (formData: FormData) => {
        try {
            if (editingDoc) {
                if (isExternal) {
                    await updateExternalDoc({
                        docId: editingDoc.id,
                        data: formData,
                        projectId: resourceId || projectId
                    }).unwrap();
                } else {
                    await updateInternalDoc({
                        tenant: tenant!,
                        docId: editingDoc.id,
                        data: formData,
                        projectId
                    }).unwrap();
                }
                toast.success('Document updated successfully');
            } else {
                const targetProjectId = resourceId || projectId;

                if (isExternal) {
                    formData.append('project', targetProjectId.toString());
                    await createExternalDoc({
                        data: formData,
                        projectId: targetProjectId
                    }).unwrap();
                } else {
                    formData.append('project', projectId.toString());
                    await createInternalDoc({
                        tenant: tenant!,
                        data: formData,
                        projectId: projectId
                    }).unwrap();
                }
                toast.success('Document created successfully');
            }
            setIsEditorOpen(false);
            setEditingDoc(undefined);
        } catch (error) {
            toast.error('Failed to save document');
        }
    };

    const handleDeleteDoc = async (docId: number) => {
        try {
            if (isExternal) {
                await deleteExternalDoc({
                    docId,
                    projectId: resourceId || projectId
                }).unwrap();
            } else {
                await deleteInternalDoc({ tenant: tenant!, docId, projectId }).unwrap();
            }
            toast.success('Document deleted');
            if (viewingDoc?.id === docId) setViewingDoc(undefined);
        } catch (error) {
            toast.error('Failed to delete document');
        }
    };

    return (
        <div className="space-y-8 pb-12 animate-in fade-in duration-500">
            {/* Header & Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="tracking-tight">
                        Project Documents
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Collaborate on project documentations and files.
                    </p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search documents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 border-border/40 focus-visible:ring-primary/20 bg-background/50"
                        />
                    </div>
                    {!isReadOnly && (
                        <Button
                            onClick={() => { setEditingDoc(undefined); setIsEditorOpen(true); }}
                        >
                            <Plus className="h-5 w-5" />
                            Add Doc
                        </Button>
                    )}
                </div>
            </div>

            <WithLoadingErrorWrapper
                loading={!initialDocs && docsLoading}
                error={docsError as any}
                onRetry={refetch}
            >
                {/* Documents Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredDocs.length > 0 ? (
                        filteredDocs.map((doc) => (
                            <Card key={doc.id} className="group relative border-border/40 bg-card/40 backdrop-blur-md transition-all duration-500 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-2 flex flex-col overflow-hidden rounded-2xl">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                <CardHeader className="pb-4 relative">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="p-3 rounded-xl bg-primary/10 text-primary mb-4 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500">
                                            <FileText className="h-6 w-6" />
                                        </div>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setViewingDoc(doc)}
                                                className="h-9 w-9 rounded-lg hover:bg-primary/10 hover:text-primary"
                                                title="View Full Screen"
                                            >
                                                <Maximize2 className="h-4 w-4" />
                                            </Button>
                                            {!isReadOnly && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => { setEditingDoc(doc); setIsEditorOpen(true); }}
                                                        className="h-9 w-9 rounded-lg hover:bg-primary/10 hover:text-primary"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-9 w-9 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent className="rounded-2xl border-border/40 backdrop-blur-xl">
                                                            <AlertDialogHeader>
                                                                <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                                                                <AlertDialogDescription>
                                                                    Are you sure you want to delete "{doc.title}"? This action cannot be undone.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                <AlertDialogAction
                                                                    onClick={() => handleDeleteDoc(doc.id)}
                                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                                >
                                                                    Delete
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <CardTitle className="text-xl font-bold line-clamp-1 group-hover:text-primary transition-colors">
                                        {doc.title}
                                    </CardTitle>
                                    <div className="flex items-center gap-4 mt-2">
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                                            <Calendar className="h-3.5 w-3.5 opacity-70" />
                                            {safeFormatDate(doc.updated_on || (doc as any).updatedAt, 'MMM d, yyyy')}
                                        </div>
                                        {doc.attachment && (
                                            <Badge variant="secondary" className="bg-primary/5 text-primary border-primary/10 text-[10px] uppercase font-bold tracking-tighter">
                                                Attachment
                                            </Badge>
                                        )}
                                    </div>
                                </CardHeader>

                                <CardContent className="flex-1 pb-6 cursor-pointer relative" onClick={() => setViewingDoc(doc)}>
                                    <div className="h-[100px] rounded-xl bg-muted/20 border border-border/5 group-hover:bg-muted/30 transition-colors p-4 relative overflow-hidden flex flex-col justify-center items-center text-center">
                                        {doc.project_files === 'attachment' ? (
                                            <>
                                                <FileUp className="h-8 w-8 text-primary/40 mb-2 group-hover:scale-110 transition-transform" />
                                                <p className="text-xs font-bold uppercase tracking-tighter text-muted-foreground/60 group-hover:text-primary/60">
                                                    File Asset
                                                </p>
                                                <p className="text-[10px] text-muted-foreground/40 mt-1 truncate max-w-full italic px-2">
                                                    {doc.attachment?.split('/').pop() || 'Untitled Content'}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                                                    Open this document to view its rich content and collaborate with your team.
                                                </p>
                                                <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background/40 to-transparent group-hover:from-background/60" />
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-6 flex items-center gap-3">
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                                            <User className="h-3.5 w-3.5 text-primary" />
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[11px] font-semibold truncate leading-tight">
                                                {doc.created_by ? `${doc.created_by.first_name} ${doc.created_by.last_name || ''}` : 'Unknown User'}
                                            </span>
                                            <span className="text-[10px] text-muted-foreground truncate leading-tight italic">
                                                {doc.created_by?.email || doc.created_by_email || 'System'}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <div className="col-span-full flex flex-col items-center justify-center py-24 rounded-3xl border-2 border-dashed border-border/60 bg-muted/10 text-center shadow-inner animate-in zoom-in duration-500">
                            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-background shadow-xl border border-border/40 -rotate-3 hover:rotate-0 transition-transform duration-500">
                                <FileText className="h-10 w-10 text-primary/80" />
                            </div>
                            <h3 className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/60 bg-clip-text text-transparent italic">
                                No Documents Yet
                            </h3>
                            <p className="mt-2 max-w-sm text-sm text-muted-foreground px-6 leading-relaxed">
                                {searchTerm ? `We couldn't find anything matching "${searchTerm}". Try a different search term.` : "This project doesn't have any documentation yet. Start by adding your first document."}
                            </p>
                            {!searchTerm && !isReadOnly && (
                                <Button
                                    variant="outline"
                                    onClick={() => setIsEditorOpen(true)}
                                    className="mt-8 gap-2 rounded-xl h-11 px-8 border-primary/20 hover:border-primary/40 hover:bg-primary/5 transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Your First Doc
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </WithLoadingErrorWrapper>

            <DocEditorModal
                isOpen={isEditorOpen}
                onClose={() => { setIsEditorOpen(false); setEditingDoc(undefined); }}
                onSave={handleSaveDoc}
                initialData={editingDoc}
                isSaving={isCreating || isUpdating}
            />

            <DocViewModal
                isOpen={!!viewingDoc}
                canManage={!isReadOnly}
                doc={viewingDoc}
                onClose={() => setViewingDoc(undefined)}
                onEdit={(doc) => {
                    if (isReadOnly) return;
                    setEditingDoc(doc);
                    setIsEditorOpen(true);
                }}
                onDelete={async (docId) => {
                    if (isReadOnly) return;
                    await handleDeleteDoc(docId);
                }}
                isExternal={isExternal}
            />
        </div>
    );
};
